import { AppError } from '@/shared/lib/appError'
import { Sentry } from '@/shared/lib/sentry'

/** Modelo ONNX leve (384 dim) via Transformers.js — alinhado ao plano offline Synap. */
export const LOCAL_EMBEDDING_MODEL_ID = 'Xenova/all-MiniLM-L6-v2' as const

/** Mesmo shape mínimo usado por `@xenova/transformers` em `env`. */
type TransformersEnvLike = {
  remoteHost: string
  allowLocalModels?: boolean
  useBrowserCache?: boolean
}

function applyOptionalRemoteHost(env: TransformersEnvLike, host: string): void {
  env.remoteHost = host.endsWith('/') ? host : `${host}/`
}

/**
 * Fecha vetores comuns de falha:
 * - `allowLocalModels=false` evita `transformers.js` tentar `/Xenova/...` relativo,
 *   que cairia no fallback SPA do PWA e devolveria `<!doctype html>` em vez do JSON.
 * - `useBrowserCache=true` mantém o cache da própria lib (Cache API `transformers-cache`).
 */
function applyTransformersEnvDefaults(env: TransformersEnvLike): void {
  env.allowLocalModels = false
  env.useBrowserCache = true
}

async function reportEmbeddingsStatus(
  status: 'loading' | 'ready' | 'error',
  error?: string,
): Promise<void> {
  try {
    const { useLocalAIStore } = await import('./useLocalAIStore')
    const state = useLocalAIStore.getState()
    state.setEmbeddingsStatus?.(status, error)
  } catch {
    /* store indisponível em testes / SSR — silencioso. */
  }
}

/** Hosts Hugging Face oficiais — `hf.co` costuma contornar bloqueios/CDN diferentes de `huggingface.co`. */
const DEFAULT_TRANSFORMERS_REMOTE_HOSTS = ['https://huggingface.co/', 'https://hf.co/'] as const
const MODEL_CONFIG_PATH = `${LOCAL_EMBEDDING_MODEL_ID}/resolve/main/config.json`
const MODEL_ENDPOINT_TIMEOUT_MS = 10_000
const RESPONSE_PREVIEW_CHARS = 240

/**
 * `VITE_TRANSFORMERS_REMOTE_HOST` na mesma origem da app (ex.: Vercel) sem ficheiros em `/public/models/`
 * faz o servidor devolver `index.html` → JSON.parse falha com `<!DOCTYPE`. Ignorar e usar o Hub.
 */
function resolveTransformersRemoteHosts(): readonly string[] {
  const raw = import.meta.env.VITE_TRANSFORMERS_REMOTE_HOST?.trim()
  if (!raw) return DEFAULT_TRANSFORMERS_REMOTE_HOSTS

  const normalized = raw.endsWith('/') ? raw : `${raw}/`
  if (typeof window === 'undefined' || typeof window.location?.origin !== 'string') {
    return [normalized, ...DEFAULT_TRANSFORMERS_REMOTE_HOSTS]
  }
  try {
    const customOrigin = new URL(normalized).origin
    if (customOrigin === window.location.origin) {
      return [...DEFAULT_TRANSFORMERS_REMOTE_HOSTS]
    }
  } catch {
    return [...DEFAULT_TRANSFORMERS_REMOTE_HOSTS]
  }
  return [normalized, ...DEFAULT_TRANSFORMERS_REMOTE_HOSTS]
}

const getPwaMode = (): string => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'unknown'
  if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone'
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui'
  if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen'
  return 'browser'
}

const previewText = (text: string): string => {
  const normalized = text.replace(/\s+/g, ' ').trim()
  return normalized.length > RESPONSE_PREVIEW_CHARS
    ? `${normalized.slice(0, RESPONSE_PREVIEW_CHARS)}...`
    : normalized
}

class LocalEmbeddingEndpointError extends Error {
  status?: number
  contentType?: string | null
  endpoint: string
  preview?: string

  constructor(
    message: string,
    options: {
      endpoint: string
      status?: number
      contentType?: string | null
      preview?: string
      cause?: unknown
    },
  ) {
    super(message)
    this.name = 'LocalEmbeddingEndpointError'
    this.endpoint = options.endpoint
    this.status = options.status
    this.contentType = options.contentType
    this.preview = options.preview
    this.cause = options.cause
  }
}

const captureLocalEmbeddingFailure = (
  error: unknown,
  context: Record<string, unknown> = {},
): void => {
  try {
    Sentry.captureException?.(error, {
      tags: {
        feature: 'local-ai',
        stage: 'embedding-model-load',
        response_type: String(context.response_type ?? 'unknown'),
      },
      extra: {
        model_id: LOCAL_EMBEDDING_MODEL_ID,
        release: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : undefined,
        pwa_mode: getPwaMode(),
        browser_online: typeof navigator === 'undefined' ? undefined : navigator.onLine,
        ...context,
      },
    })
  } catch {
    /* Sentry indisponível — não bloquear carregamento do modelo. */
  }
}

const buildModelConfigUrl = (host: string): string =>
  new URL(MODEL_CONFIG_PATH, host.endsWith('/') ? host : `${host}/`).toString()

const validateTransformersModelEndpoint = async (host: string): Promise<void> => {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return

  const endpoint = buildModelConfigUrl(host)
  const controller = new AbortController()
  const timeoutId = globalThis.setTimeout(() => controller.abort(), MODEL_ENDPOINT_TIMEOUT_MS)

  try {
    const res = await fetch(endpoint, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: controller.signal,
    })
    const contentType = res.headers.get('content-type')
    const text = await res.text()
    const preview = previewText(text)

    if (!res.ok) {
      throw new LocalEmbeddingEndpointError(
        `Endpoint do modelo de embeddings indisponível (HTTP ${res.status}).`,
        { endpoint, status: res.status, contentType, preview },
      )
    }

    if (/^<(!doctype|html)/i.test(preview)) {
      throw new LocalEmbeddingEndpointError(
        'Endpoint do modelo de embeddings retornou HTML.',
        { endpoint, status: res.status, contentType, preview },
      )
    }

    try {
      JSON.parse(text)
    } catch (cause) {
      if (!contentType?.toLowerCase().includes('application/json')) {
        throw new LocalEmbeddingEndpointError(
          'Endpoint do modelo de embeddings retornou conteúdo não JSON.',
          { endpoint, status: res.status, contentType, preview, cause },
        )
      }
      throw new LocalEmbeddingEndpointError(
        'Endpoint do modelo de embeddings retornou JSON inválido.',
        { endpoint, status: res.status, contentType, preview, cause },
      )
    }
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      throw new LocalEmbeddingEndpointError(
        'Timeout ao validar endpoint do modelo de embeddings.',
        { endpoint, cause: err },
      )
    }
    throw err
  } finally {
    globalThis.clearTimeout(timeoutId)
  }
}

/** HF às vezes devolve HTML (proxy, captive portal, cache ruim); JSON.parse falha com `<!DOCTYPE`. */
function looksLikeHtmlInsteadOfJsonError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return (
    (/unexpected token/i.test(msg) && /<!doctype|<html/i.test(msg)) ||
    /not valid json/i.test(msg) ||
    /<(!doctype|html)/i.test(msg)
  )
}

async function clearTransformersBrowserCache(): Promise<void> {
  if (typeof caches === 'undefined') return
  try {
    await caches.delete('transformers-cache')
  } catch {
    /* quota / iframe / modo privado */
  }
}

export const LOCAL_EMBEDDING_DIMENSION = 384

const EMBED_BATCH_CONCURRENCY = 5

/** Saída mínima do pipeline `feature-extraction` (Tensor Xenova). */
type EmbeddingTensorLike = {
  data: Float32Array | Float64Array | number[]
  dims?: number[]
}

type FeatureExtractionPipelineFn = (
  text: string,
  options?: { pooling?: string; normalize?: boolean },
) => Promise<EmbeddingTensorLike>

export class LocalEmbeddingEngine {
  private pipeline: FeatureExtractionPipelineFn | null = null
  private loading: Promise<void> | null = null

  async ensureLoaded(): Promise<void> {
    if (this.pipeline) return
    if (this.loading) {
      await this.loading
      return
    }

    this.loading = (async () => {
      void reportEmbeddingsStatus('loading')
      const loadPipeline = async (): Promise<void> => {
        const { env: tfEnv, pipeline } = await import('@xenova/transformers')
        applyTransformersEnvDefaults(tfEnv as unknown as TransformersEnvLike)
        const hosts = resolveTransformersRemoteHosts()

        let lastErr: unknown
        for (let i = 0; i < hosts.length; i++) {
          const h = hosts[i]!
          applyOptionalRemoteHost(tfEnv, h)
          try {
            await validateTransformersModelEndpoint(h)
            const pipe = await pipeline('feature-extraction', LOCAL_EMBEDDING_MODEL_ID)
            this.pipeline = pipe as unknown as FeatureExtractionPipelineFn
            return
          } catch (err) {
            lastErr = err
            const endpointError = err instanceof LocalEmbeddingEndpointError
            const htmlJson = endpointError || looksLikeHtmlInsteadOfJsonError(err)
            if (htmlJson) {
              await clearTransformersBrowserCache()
              if (i < hosts.length - 1) continue
            }
            throw err
          }
        }
        throw lastErr
      }

      let cause: unknown
      try {
        await loadPipeline()
      } catch (first) {
        cause = first
        if (looksLikeHtmlInsteadOfJsonError(first)) {
          await clearTransformersBrowserCache()
          try {
            await loadPipeline()
            cause = undefined
          } catch (second) {
            cause = second
          }
        }
      }

      if (cause !== undefined) {
        this.pipeline = null
        const endpointErr =
          cause instanceof LocalEmbeddingEndpointError ? cause : null
        captureLocalEmbeddingFailure(cause, {
          endpoint: endpointErr?.endpoint,
          status: endpointErr?.status,
          content_type: endpointErr ? endpointErr.contentType ?? 'missing' : undefined,
          response_preview: endpointErr?.preview,
          response_type: endpointErr
            ? 'invalid_endpoint_response'
            : looksLikeHtmlInsteadOfJsonError(cause)
              ? 'html_parse_error'
              : 'load_error',
        })
        const friendly =
          'IA local de embeddings indisponível. O chat continuará funcionando no modo online com a chave configurada (BYOK ou padrão da plataforma).'
        void reportEmbeddingsStatus('error', friendly)
        throw new AppError(friendly, {
          code: 'LOCAL_AI_EMBEDDINGS_UNAVAILABLE',
          cause,
        })
      }
      void reportEmbeddingsStatus('ready')
    })().finally(() => {
      this.loading = null
    })

    await this.loading
  }

  private tensorToEmbedding(raw: EmbeddingTensorLike): Float32Array {
    const { data, dims = [] } = raw
    const flat =
      data instanceof Float32Array
        ? data
        : data instanceof Float64Array
          ? new Float32Array(data)
          : new Float32Array(data)

    if (flat.length === LOCAL_EMBEDDING_DIMENSION) {
      return new Float32Array(flat)
    }

    const lastDim = dims.length > 0 ? dims[dims.length - 1] : 0
    const batch = dims.length > 1 ? dims[0] ?? 1 : 1
    if (lastDim === LOCAL_EMBEDDING_DIMENSION && flat.length >= LOCAL_EMBEDDING_DIMENSION) {
      return new Float32Array(flat.buffer, flat.byteOffset, LOCAL_EMBEDDING_DIMENSION)
    }

    if (flat.length === batch * LOCAL_EMBEDDING_DIMENSION) {
      return new Float32Array(flat.subarray(0, LOCAL_EMBEDDING_DIMENSION))
    }

    throw new AppError(
      `Embedding local com dimensão inválida (esperado ${LOCAL_EMBEDDING_DIMENSION}, obtido ${flat.length}).`,
      { code: 'LOCAL_AI_INFERENCE_FAILED' },
    )
  }

  async embed(text: string): Promise<Float32Array> {
    await this.ensureLoaded()
    if (!this.pipeline) {
      throw new AppError('Pipeline de embeddings não inicializado.', {
        code: 'LOCAL_AI_NOT_READY',
      })
    }

    try {
      const output = await this.pipeline(text, {
        pooling: 'mean',
        normalize: true,
      })
      return this.tensorToEmbedding(output)
    } catch (error) {
      if (error instanceof AppError) throw error
      throw new AppError('Falha ao gerar embedding local.', {
        code: 'LOCAL_AI_INFERENCE_FAILED',
        cause: error,
      })
    }
  }

  async embedBatch(texts: string[]): Promise<Float32Array[]> {
    if (texts.length === 0) return []

    const results: Float32Array[] = new Array(texts.length)

    for (let offset = 0; offset < texts.length; offset += EMBED_BATCH_CONCURRENCY) {
      const slice = texts.slice(offset, offset + EMBED_BATCH_CONCURRENCY)
      const batch = await Promise.all(slice.map((t) => this.embed(t)))
      batch.forEach((vec, i) => {
        results[offset + i] = vec
      })
    }

    return results
  }

  cosineSimilarity(a: Float32Array, b: Float32Array): number {
    if (a.length !== b.length) {
      throw new AppError('Vetores de embedding com tamanhos diferentes.', {
        code: 'VALIDATION',
        status: 400,
      })
    }

    let dot = 0
    let normA = 0
    let normB = 0
    for (let i = 0; i < a.length; i++) {
      const x = a[i]
      const y = b[i]
      dot += x * y
      normA += x * x
      normB += y * y
    }

    const denom = Math.sqrt(normA) * Math.sqrt(normB)
    if (denom === 0) return 0

    const score = dot / denom
    return Math.max(-1, Math.min(1, score))
  }
}

export const embeddingEngine = new LocalEmbeddingEngine()
